import type { ReactNode as Children } from 'react';
import { useTheme } from '@mui/material/styles';
import { Box, Chip, Typography, useMediaQuery } from '@mui/material';
import React, { Fragment } from 'react';

import { useHistory } from 'react-router-dom';
import { sortArray } from '../../utils/sorting.utils';
import { ListItem } from '../../shared/types';
import { ViewTracker } from './ViewTracker';

import Link from 'components/ui/AppLink';

export enum ListItemOrientation {
    Horizontal,
    Vertical,
}

export enum ListItemType {
    Chip,
    Link,
}

declare type ChipColors =
    | 'default'
    | 'error'
    | 'warning'
    | 'success'
    | 'info'
    | 'neutral'
    | 'primary'
    | 'secondary';

export interface LinkListProps {
    items: ListItem[];
    orientation: ListItemOrientation;
    displayType: ListItemType;
    chipVariant?: ChipColors;
    disableSort?: boolean;
    linkClassName?: string;
    isSpacedVerticalLinks?: boolean;
    source?: string;
    isHorizontalListOneLine?: boolean;
    link?: Children;
}

const LinkList = (props: LinkListProps): JSX.Element => {
    const history = useHistory();
    const theme = useTheme();
    const sm = useMediaQuery(theme.breakpoints.down('sm'));

    const chipClick = (item: ListItem) => {
        history.push(item.url);
    };

    const isHorizontal = props.orientation === ListItemOrientation.Horizontal;

    const sortedItems = getSortedItems(props.items, props.disableSort);

    function getSortedItems(
        items: ListItem[],
        disableSort?: boolean
    ): ListItem[] {
        if (!items) {
            return [];
        }

        return disableSort ? items : sortArray(props.items, 'name');
    }

    const clickableItems = sortedItems.map((item, i) => {
        if (props.displayType === ListItemType.Chip) {
            return (
                <Chip
                    key={i}
                    sx={{ mr: 1, mt: 1 }}
                    label={item.name}
                    icon={item.icon}
                    color={
                        props.chipVariant === 'primary' ? 'primary' : 'default'
                    }
                    variant={
                        props.chipVariant === 'primary' ? 'solid' : 'outlined'
                    }
                    onClick={() => chipClick(item)}
                />
            );
        }

        return (
            <Fragment key={i}>
                {props.orientation === ListItemOrientation.Horizontal &&
                    i > 0 && (
                        <Typography
                            component="span"
                            variant="body2"
                            sx={{ fontSize: 'inherit' }}
                        >
                            ,&nbsp;
                        </Typography>
                    )}
                <Typography
                    sx={
                        props.isSpacedVerticalLinks
                            ? { '&:not(:last-child)': { mb: 2 } }
                            : undefined
                    }
                >
                    <ViewTracker source={props.source || ''}>
                        <Link
                            className={props.linkClassName}
                            sx={{
                                fontSize: 'inherit',
                                mt: isHorizontal ? 0 : 1,
                            }}
                            to={item.url}
                        >
                            {item.name}
                        </Link>
                    </ViewTracker>
                </Typography>
            </Fragment>
        );
    });

    return (
        <Box
            sx={
                isHorizontal
                    ? {
                          display: 'flex',
                          flexDirection: 'row',
                          flexWrap:
                              props.isHorizontalListOneLine && sm
                                  ? 'initial'
                                  : 'wrap',
                          mt: -1,
                      }
                    : {
                          display: 'flex',
                          flexDirection: 'column',
                          overflow: 'hidden',
                      }
            }
        >
            {clickableItems}
            {props.link}
        </Box>
    );
};

export default LinkList;
